import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/library/accordion";
import DocumentItem, {
  type DealMoveOption,
  type FolderMoveOption,
} from "./DocumentItem";
import type { AttachmentLabel } from "@/utils/attachmentLabels";

export default function Documents({
  documents,
  onDelete,
  onEdit,
  onRename,
  requiredDocNames = [],
  moveOptions,
  onMoveToFolder,
  onAttachmentLabelChange,
  dealOptions,
  onMoveToDeal,
}: {
  documents: any[];
  onDelete?: (doc: any) => void;
  onEdit?: (doc: any) => void;
  onRename?: (id: string, newName: string) => void;
  requiredDocNames?: string[];
  moveOptions?: FolderMoveOption[];
  onMoveToFolder?: (docId: string, folderId: number | null) => void;
  onAttachmentLabelChange?: (docId: string, label: AttachmentLabel) => void;
  /** The lead + its deals, for showing/correcting each file's grain. */
  dealOptions?: DealMoveOption[];
  onMoveToDeal?: (docId: string, childId: number | null) => void;
}) {
  // Group documents by category
  const groupedDocuments = documents?.reduce(
    (acc: any, doc: any) => {
      if (!acc[doc.category]) {
        acc[doc.category] = [];
      }
      acc[doc.category].push(doc);
      return acc;
    },
    {} as Record<string, any[]>
  );

  if (!documents || documents.length === 0) {
    return (
      <div className="m-0 py-6 text-center text-b4 text-[var(--color-black-6)]">
        No documents uploaded yet
      </div>
    );
  }

  return (
    <Accordion
      type="multiple"
      className="w-full space-y-3"
      defaultValue={Object.keys(groupedDocuments || {})}
    >
      {Object.entries(groupedDocuments || {}).map(([category, docs]) => (
        <AccordionItem key={category} value={category} className="border-none">
          <AccordionTrigger className="hover:no-underline py-0 mb-3 !justify-start gap-2 items-center [&>svg]:translate-y-0">
            <span className="text-b3-mid m-0 text-[var(--color-black-10)]">
              {category}
            </span>
          </AccordionTrigger>
          <AccordionContent className="pt-0 pb-0">
            <div className="flex flex-col gap-3">
              {(docs as any)?.map((doc: any) => {
                const isRequired = requiredDocNames.some(
                  (name: string) =>
                    name.toLowerCase() === doc.name?.toLowerCase()
                );
                return (
                  <DocumentItem
                    key={doc.id}
                    doc={doc}
                    onDelete={onDelete}
                    onEdit={onEdit}
                    onRename={onRename}
                    isRequired={isRequired}
                    moveOptions={moveOptions}
                    onMoveToFolder={onMoveToFolder}
                    onAttachmentLabelChange={onAttachmentLabelChange}
                    dealOptions={dealOptions}
                    onMoveToDeal={onMoveToDeal}
                  />
                );
              })}
            </div>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}
